import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2, Send } from "lucide-react";

export default function AdminAiSettings() {
  const [value, setValue] = useState("");
  const [isCustom, setIsCustom] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testQ, setTestQ] = useState("Miből készül a GrowBag föld?");
  const [testA, setTestA] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/admin/chat-prompt").then((r) => { setValue(r.data.value); setIsCustom(r.data.is_custom); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/chat-prompt", { value });
      setIsCustom(true);
      toast.success("Mentve – az asszisztens mostantól ezt követi");
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || "Nem sikerült menteni");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    const r = await api.delete("/admin/chat-prompt");
    setValue(r.data.value); setIsCustom(false);
    toast.success("Alapértelmezett visszaállítva");
  };

  const runTest = async () => {
    setTesting(true); setTestA("");
    try {
      const r = await api.post("/chat", { message: testQ, provider: "anthropic" });
      setTestA(r.data.reply);
    } catch { toast.error("A chat nem válaszolt"); } finally { setTesting(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-ai-settings">
      <div>
        <p className="section-label">AI asszisztens</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#201B18] mt-1">Mit mondhat az „Ask GrowBag”?</h1>
        <p className="text-sm text-[#8C8075] mt-2 max-w-2xl">
          Itt írod meg a tényeket és szabályokat. Amit ide beírsz, az asszisztens csak azt mondhatja – ami nincs benne, arra azt válaszolja, hogy nem tudja. Mentés után azonnal él.
          {isCustom ? <span className="ml-2 chip chip-sage">egyedi</span> : <span className="ml-2 chip">alapértelmezett</span>}
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl bg-white border border-[#2C221E]/10 p-5 space-y-4">
          <Textarea value={value} onChange={(e) => setValue(e.target.value)} rows={26} data-testid="ai-prompt-textarea" className="font-mono text-xs leading-relaxed" />
          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={save} disabled={saving} data-testid="ai-prompt-save" className="bg-[#2C221E] hover:bg-[#1a1310] text-[#FBF8F3] rounded-full">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Mentés</>}
            </Button>
            <Button variant="outline" onClick={reset} data-testid="ai-prompt-reset" className="rounded-full">
              <RotateCcw className="h-4 w-4 mr-2" /> Alapértelmezett
            </Button>
          </div>
        </div>

        <div className="rounded-2xl bg-[#F3EDE2] border border-[#C29B78]/30 p-5 space-y-3">
          <p className="section-label">Próbáld ki</p>
          <p className="text-xs text-[#5C524A]">Mentés után tedd fel a kérdést, és nézd meg, mit válaszol.</p>
          <Input value={testQ} onChange={(e) => setTestQ(e.target.value)} data-testid="ai-test-question" />
          <Button onClick={runTest} disabled={testing} data-testid="ai-test-send" className="w-full bg-[#4A6B53] hover:bg-[#3B5843] text-white rounded-full">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Kérdezz</>}
          </Button>
          {testA && <p className="text-sm text-[#201B18] bg-white rounded-xl p-3 border border-[#2C221E]/10" data-testid="ai-test-answer">{testA}</p>}
        </div>
      </div>
    </div>
  );
}
